/**
 * Der Kontext, in dem Legalität entschieden wird.
 *
 * Bündelt die vier Dinge, die jede Regelfunktion braucht: die Brettgeometrie, die
 * Feldbenennung, die RuleConfig **dieser Partie** (nie die globale, §53) und die
 * aktuelle Figurenplatzierung. Dazu die vorberechneten Ringfelder, weil fast jede
 * Bewegungsprüfung sie braucht.
 *
 * Der Kontext ist unveränderlich und enthält keinen Zustand über den BoardState hinaus —
 * keine Zugreihenfolge, keine Punkte, keine Uhr. Das macht ihn in der Simulationsschleife
 * billig: Board und Layout werden einmal gebaut, nur `state` wechselt pro Zug.
 */
import { isOccupied } from './board-state.js';
import { ringCells } from './ring.js';
import { startZoneCells } from './start-zone.js';
export function createRuleContext(input) {
    return {
        ...input,
        ring: input.config.ring.enabled ? ringCells(input.board) : [],
    };
}
/**
 * Gehören zwei Spieler zu verschiedenen Seiten? Eine Seite ist das Team, ohne Team der Spieler
 * selbst — dieselbe Regel wie `sideOf` in `game-state.ts`, hier aus dem Kontext statt aus der
 * Partie. Schlagen darf man nur einen Gegner (Nr. 54); **stoßen** dagegen jede Figur (`R2`).
 */
export function areOpponents(context, a, b) {
    if (a === b)
        return false;
    const team = context.teams.get(a);
    return team === undefined || team !== context.teams.get(b);
}
/** Derselbe Kontext mit einem anderen BoardState — für `simulateAction` (§90). */
export function withBoardState(context, state) {
    return { ...context, state };
}
/**
 * Die freien Felder der eigenen Startzone eines Spielers.
 *
 * **Eine Definition für zwei Regeln.** Die Aufstellung (§10) verteilt Figuren auf freie Felder
 * der eigenen Startzone, und der Respawn (§22) setzt eine geschlagene Figur auf genau dieselbe
 * Menge. Zweimal berechnet würden die beiden irgendwann auseinanderlaufen — etwa wenn sich die
 * Bedeutung von `startZone.rows` ändert.
 *
 * Steht hier, weil dieser Kontext genau die vier Dinge bündelt, die dafür nötig sind: Layout,
 * Konfiguration, Sektorzuordnung und Belegung. `setup.ts` und `legal-respawns.ts` rufen es auf;
 * der Unterschied zwischen ihnen ist allein der Schalter `respawn.enabled`, und der betrifft
 * §22 und nicht die Aufstellung.
 *
 * Ein Spieler ohne Sektor hat keine Startzone — und keine Felder.
 */
export function freeStartZoneCells(context, player) {
    const sector = context.sectors.get(player);
    if (sector === undefined)
        return [];
    return startZoneCells(context.layout, sector, { rows: context.config.startZone.rows }).filter((cell) => !isOccupied(context.state, cell));
}
//# sourceMappingURL=rule-context.js.map